<template>
    <div class="dunning-root fs-3">
        <h3 class="pb-1 bb-2">{{ $l10n.__('Définition de votre plan de relance') }}</h3>

        <p class="fs-3 mt-3 gray-60">
            {{ $l10n.__('Définissez et organisez votre plan de relance.') }}
        </p>

        <div v-if="steps.length > 0" class="well flex-row">
            <h3 class="m-0 step-day">{{ $l10n.__('Jours') }}</h3>
            <h3 class="m-0 step-name">{{ $l10n.__('Nom de la relance') }}</h3>
            <h3 class="m-0 step-contact mr-3">{{ $l10n.__('Destinataire') }}</h3>
        </div>

        <!-- BEFORE DUNNING -->
        <ul v-if="stepsBefore.length > 0" class="before-dunning">
            <li v-for="step in stepsBefore" :key="step.id">
                <div class="well">
                    <span class="step-day">
                        {{ $l10n.n__('%d jour', '%d jours', Math.abs(step.days), step.days) }}
                    </span>
                    <span class="step-name semibold">{{ step.name }}</span>
                    <span class="step-contact">{{ recipientText(step.recipient_type) }}</span>

                    <el-dropdown>
                        <CompassIcon type="primary" size="small" icon="settings" />
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item @click.native="edit(step.id)">{{
                                    $l10n.__('Modifier')
                                }}</el-dropdown-item>
                                <el-dropdown-item
                                    @click.native="deleteStep({ stepId: step.id, planId: planId })"
                                >
                                    {{ $l10n.__('Supprimer') }}
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </div>
            </li>
        </ul>

        <!-- DEADLINE -->
        <p class="well m-0 black-bkg white b-0">
            {{ $l10n.__('Date d’échéance de votre document') }}
        </p>

        <!-- AFTER DUNNING -->
        <ul class="after-dunning">
            <li v-for="step in stepsAfter" :key="step.id">
                <div class="well">
                    <span class="step-day">
                        +{{ $l10n.n__('%d jour', '%d jours', Math.abs(step.days), step.days) }}
                    </span>
                    <span class="step-name semibold">{{ step.name }}</span>
                    <span class="step-contact">{{ recipientText(step.recipient_type) }}</span>

                    <el-dropdown>
                        <CompassIcon type="primary" size="small" icon="settings" />
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item @click.native="edit(step.id)">{{
                                    $l10n.__('Modifier')
                                }}</el-dropdown-item>
                                <el-dropdown-item @click.native="openDeletePopup(step.id)">{{
                                    $l10n.__('Supprimer')
                                }}</el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </div>
            </li>
        </ul>

        <CompassButton
            :label="$t.__('preferences.Dunning.Step.add_dunning')"
            type="tertiary"
            class="mt-2"
            @click="create()"
        />

        <CompassDialog
            :visible.sync="confirmationVisible"
            size="small"
            :title="$t.__('preferences.Dunning.Step.deleting_dunning')"
            class="step-modal"
        >
            <template #default>
                <div class="dialog__default">
                    <span class="typographies-paragraph-medium-regular">
                        {{
                            $l10n.__('Confirmez-vous vouloir supprimer la relance "%(stepName)s"', {
                                stepName: stepNameToDelete,
                            })
                        }}
                    </span>
                </div>
            </template>
            <template #footer>
                <div class="dialog__footer">
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        @click="confirmationVisible = false"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.delete')"
                        type="primary-destructive"
                        :loading="loading"
                        class="ml-3"
                        @click="confirmDelete()"
                    />
                </div>
            </template>
        </CompassDialog>

        <!-- FORM DUNNINGS -->
        <step-form
            v-if="formVisible"
            :planId="planId"
            :visible.sync="formVisible"
            :recipientTypeList="recipientTypeList"
            :init="current"
        />
    </div>
</template>

<script>
import StepForm from './StepForm.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('dunning');

export default {
    components: {
        StepForm,
    },
    props: {
        planId: {
            required: true,
            type: Number,
        },
        /**
         * Each entry must have :
         * + id : Required
         * + name : Required
         * + days : Number of day between deadline and this step; Required
         */
        steps: {
            required: true,
            type: Array,
            validator: (steps) => {
                let correct = true;
                const position = ['before', 'after'];
                steps.forEach((step) => {
                    correct =
                        correct &&
                        step.hasOwnProperty('id') &&
                        Number.isInteger(step.id) &&
                        step.id > 0;
                    correct = correct && step.hasOwnProperty('name') && step.name != '';
                    correct = correct && step.hasOwnProperty('days') && Number.isInteger(step.days);
                });
                return correct;
            },
        },
    },
    data() {
        return {
            /**
             * Display form popup
             */
            formVisible: false,
            /**
             * Display delete popup
             */
            confirmationVisible: false,
            stepToDelete: 0,
            /**
             * Id of the step in the editing process.
             * When it's 0, no step selected.
             */
            stepToEdit: 0,
            recipientTypeList: [
                {
                    code: 'dunning',
                    label: this.$l10n.__('Le contact de relance du client'),
                },
                {
                    code: 'invoicing',
                    label: this.$l10n.__('Le contact du document'),
                },
                {
                    code: 'main',
                    label: this.$l10n.__('Le contact principal'),
                },
                {
                    code: 'staff',
                    label: this.$l10n.__('Un collaborateur interne'),
                },
                {
                    code: 'billing',
                    label: this.$l10n.__('Le contact de facturation'),
                },
            ],
        };
    },
    computed: {
        /* Selector */
        stepsBefore() {
            return this.sortedStep.filter((step) => step.days < 0);
        },
        stepsAfter() {
            return this.sortedStep.filter((step) => step.days >= 0);
        },

        /* Prepare form */
        current() {
            const currentStep = this.steps.find((step) => step.id == this.stepToEdit);
            if (currentStep !== undefined) {
                if (currentStep.sender_type != 'staff') {
                    currentStep.sender_id = '';
                }
                if (currentStep.recipient_type != 'staff') {
                    currentStep.recipient_id = '';
                }
                return currentStep;
            }
            return {
                id: null,
                name: '',
                days: null,
                email_template_id: null,
                sender_type: 'doc_speaker',
                sender_id: '',
                recipient_type: 'invoicing',
                recipient_id: '',
                enable: true,
            };
        },

        /**
         * return the minimum day, when it's 0, there is no min born
         */
        sortedStep() {
            const steps = this.steps;
            steps.sort((a, b) => {
                if (a.days > b.days) {
                    return 1;
                } else if (a.days < b.days) {
                    return -1;
                }
                return 0;
            });
            return steps;
        },
        stepNameToDelete() {
            const step = this.steps.find((step) => step.id == this.stepToDelete);
            if (typeof step === 'undefined') {
                return '';
            }
            return step.name;
        },
        ...mapState(['loading']),
    },
    created() {
        EventBus.on('update:visible', (visibility) => {
            this.formVisible = visibility;
        });
    },
    beforeDestroy() {
        EventBus.off('update:visible');
    },
    methods: {
        edit(id) {
            this.stepToEdit = id;
            this.formVisible = true;
        },
        create() {
            this.stepToEdit = null;
            this.formVisible = true;
        },
        recipientText(recipientType) {
            const type = this.recipientTypeList.find((t) => t.code == recipientType);
            if (typeof type === 'undefined') {
                return '';
            }
            return type.label;
        },
        openDeletePopup(stepId) {
            this.stepToDelete = stepId;
            this.confirmationVisible = true;
        },
        confirmDelete() {
            const payload = {
                stepId: this.stepToDelete,
                planId: this.planId,
            };
            this.deleteStep(payload).then(() => {
                this.confirmationVisible = false;
                this.stepToDelete = 0;
                Push.info(this.$l10n.__('Etape correctement supprimée'));
            });
        },
        ...mapActions(['deleteStep']),
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.step-modal {
    .dialog {
        &__default {
            padding-bottom: var(--spacing-4);
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 var(--spacing-5) var(--spacing-4);
        }
    }
}

ul {
    margin: 0;
}
.well {
    border-radius: 0;
}

.step-day {
    width: 160px;
}
.step-name {
    flex: 1;
}
.step-contact {
    width: 320px;
}

li {
    position: relative;
    display: flex;
    align-items: center;

    .chip {
        border: solid $primary-50 1px;
        background-color: transparent;
    }

    .well {
        flex: 1;
        display: flex;
        align-items: center;
        border-radius: 0;

        .el-dropdown {
            margin-left: auto;
        }
    }
}
</style>
